import tw from 'twin.macro'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CgSearch } from 'react-icons/cg'
import { isEmpty } from 'lodash'

import { useAuth } from 'modules/Auth/hooks/useAuth'
import { useListenUserProfileQuery } from 'modules/Users/userApi'
import {
  useListenCouponsQuery,
  useListenLegacyProductPricingQuery,
  useListenOneTimeProductsQuery,
  useListenProductCurrencyQuery,
  useListenProductPricingQuery,
  useListenStripeProductIdQuery,
  useSetProductPricesMutation,
} from '../productInfoApi'

import { STRIPE_SETUP } from 'modules/App/components/Header'
import { createUID } from 'common/utils/createUID'
import { Dialog, DialogContent, DialogTrigger } from 'common/components/Dialog/Dialog'
import { Button, buttonBase, buttonSizes, buttonVariants } from 'common/components/Button/Button'
import { FormRefsControlProvider } from 'common/components/RefsControl/FormRefsControl/context'
import { InputGroup } from 'common/components/Input/InputGroup'
import { Input } from 'common/components/Input/Input'
import { useAlert } from 'common/components/Alert/hooks/useAlert'

import { AddCouponForm } from '../components/CouponForm/AddCouponForm'
import CouponsList from '../components/CouponsList/CouponsList'
import { getProductCurrency } from '../utils/utils'

export default function Coupons() {
  const { createAlert } = useAlert()
  const navigate = useNavigate()
  const { userId } = useAuth()

  const [addCouponDialogOpen, setAddCouponDialogOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const [setProductPrices, { isLoading: isUpdatingProductPrices }] = useSetProductPricesMutation()

  const { data: profile } = useListenUserProfileQuery({ userId })
  const coachOrgId = profile?.coachOrgId || ''

  const { data: couponsData } = useListenCouponsQuery({ coachOrgId })
  const couponsLoading = couponsData === undefined || couponsData?.isLoading
  const coupons = couponsData || {}

  const { data: legacyPlansData } = useListenLegacyProductPricingQuery({ coachOrgId })
  const legacyProductPlansLoading = legacyPlansData === undefined || legacyPlansData?.isLoading
  const legacyProductPlans = legacyProductPlansLoading || !legacyPlansData ? [] : legacyPlansData

  const { data: plansData } = useListenProductPricingQuery({ coachOrgId })
  const productPlansLoading = plansData === undefined || plansData?.isLoading
  const productPlans = productPlansLoading || !plansData ? {} : plansData

  const { data: oneTimeProductsData } = useListenOneTimeProductsQuery({ coachOrgId })
  const products = oneTimeProductsData?.isLoading || !oneTimeProductsData ? {} : oneTimeProductsData

  const { data: stripeProductId } = useListenStripeProductIdQuery({ coachOrgId })
  const stripeProductIdLoading = stripeProductId === undefined || stripeProductId?.isLoading

  const { data: currency } = useListenProductCurrencyQuery({ coachOrgId })
  const currencyLoading = currency === undefined || currency?.isLoading

  const loading =
    couponsLoading ||
    legacyProductPlansLoading ||
    productPlansLoading ||
    stripeProductIdLoading ||
    isUpdatingProductPrices ||
    currencyLoading

  useEffect(() => {
    // Migrate legacy array based plans to new object based plans
    if (!productPlansLoading && !legacyProductPlansLoading && isEmpty(productPlans) && !isEmpty(legacyProductPlans)) {
      let updatedPlans = {}
      legacyProductPlans.forEach((plan, index) => {
        const planId = createUID()
        updatedPlans[planId] = {
          ...plan,
          idx: plan?.idx || index,
          id: planId,
          stripe_plan_id: plan?.stripe_price_id || plan?.stripe_plan_id,
        }
      })
      setProductPrices({ coachOrgId, plans: updatedPlans })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productPlansLoading, legacyProductPlansLoading])

  useEffect(() => {
    if (!loading && !stripeProductId) {
      createAlert({ text: 'Please create Stripe product first', type: 'danger' })
      navigate(STRIPE_SETUP)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, stripeProductId])

  return (
    <div className='flex flex-col min-h-screen bg-offWhite px-16'>
      <div className='w-full max-w-7xl mx-auto mt-12'>
        <div className='flex items-start justify-between'>
          <div className='w-1/4 mr-6'>
            <h1 className='font-bold text-5xl text-tBlack'>Coupons</h1>
          </div>
          <div className='flex-1 flex justify-center w-2/4'>
            <InputGroup css={[tw`max-w-md ring-offWhite`, searchQuery.length && tw`ring-tGreen ring-2`]}>
              <CgSearch tw='w-5 h-5' />
              <Input
                type='text'
                placeholder='Search for coupon'
                onChange={(e) => setSearchQuery(e.target.value)}
                value={searchQuery}
                hideRing
              />
              {searchQuery.length && (
                <Button variant='secondary' size='md' onClick={() => setSearchQuery('')}>
                  Clear
                </Button>
              )}
            </InputGroup>
          </div>
          <div className='flex justify-end ml-6 w-1/4'>
            <Dialog open={addCouponDialogOpen} setOpen={setAddCouponDialogOpen}>
              <DialogTrigger css={[buttonBase, buttonVariants.primary, buttonSizes.lg]}>Create coupon</DialogTrigger>
              <DialogContent header='Create coupon' overlayProps={{ 'data-no-dnd': true }}>
                <FormRefsControlProvider>
                  <AddCouponForm
                    coachOrgId={coachOrgId}
                    coupons={coupons}
                    closeForm={() => setAddCouponDialogOpen(false)}
                  />
                </FormRefsControlProvider>
              </DialogContent>
            </Dialog>
          </div>
        </div>
        <div className='flex flex-col my-20'>
          <CouponsList
            coachOrgId={coachOrgId}
            coupons={coupons}
            plans={productPlans}
            products={products}
            currency={getProductCurrency({ defaultCurrency: currency })}
            searchQuery={searchQuery}
            setAddCouponDialogOpen={setAddCouponDialogOpen}
            loading={loading}
          />
        </div>
      </div>
    </div>
  )
}
